import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import { Swiper, SwiperSlide } from 'swiper/react';

import {
  Button,
  Checkbox,
  Input,
  Prose,
  SeeMore,
  Select,
  TextArea,
} from '@/atoms';

import type { FunctionComponent } from 'react';
import type { SwiperClass } from 'swiper/react';
import type { TypeContactFormLandingProps } from './types';

const PhoneInput = dynamic(() => import('@/atoms/phone-input'), {
  ssr: false,
});

/**
 * ContactFormLanding
 */
export const ContactFormLanding: FunctionComponent<
  TypeContactFormLandingProps
> = ({
  textPolicies,
  buildingsSelectValues,
  mapOfAccommodation,
  watchBuilding,
  hearUsSelect,
  defaultBuilding,
  defaultApartment,
  hearUsDefaultValue,
  methods,
  validatePhone,
  validateEmails,
  resetForm,
  phoneCountry,
  setPhoneCountry,
}: TypeContactFormLandingProps) => {
  const { t, lang } = useTranslation('common');
  const [currentStep, setCurrentStep] = useState(0);
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [hasRequiredFields, sethasRequiredFields] = useState(false);
  const swiperRef = useRef<SwiperClass | null>(null);
  const {
    formState: { errors },
    getValues,
  } = useFormContext();

  const handleFormReset = () => {
    setReadyToSubmit(false);
    handleStep(0);
  };

  const handleStep = async (index: number) => {
    const { fieldsToValidate } = pages[currentStep];

    if (
      !resetForm &&
      (index > currentStep || (index < currentStep && readyToSubmit))
    ) {
      let isEmailValid = true;
      let isPhoneValid = true;
      const isValid = await methods.trigger(
        fieldsToValidate as
          | (
              | 'building'
              | 'name'
              | 'lastName'
              | 'email'
              | 'emailConfirm'
              | 'telephone'
              | 'hearUs'
              | 'accommodation'
              | 'addMore'
              | 'marketingOptIn'
              | 'marketingGreystar'
            )[]
          | undefined
      );

      if (!isValid) {
        if (swiperRef.current) {
          swiperRef.current.updateAutoHeight();
        }
        return;
      }

      if (fieldsToValidate && fieldsToValidate.includes('email')) {
        const email = methods.getValues('email');
        const emailConfirm = methods.getValues('emailConfirm');
        isEmailValid = validateEmails(email, emailConfirm);
      }

      if (fieldsToValidate && fieldsToValidate.includes('telephone')) {
        const phoneNumber = methods.getValues('telephone');
        isPhoneValid = await validatePhone(phoneNumber, phoneCountry);
      }

      if (!isEmailValid || !isPhoneValid) {
        if (swiperRef.current) {
          swiperRef.current.updateAutoHeight();
        }
        return;
      }
    }

    if (index > -1) {
      const nextStepFieldsToValidate = pages[index].fieldsToValidate ?? [];
      setCurrentStep(index);
      sethasRequiredFields(
        nextStepFieldsToValidate && nextStepFieldsToValidate.length > 0
      );
    }
  };

  useEffect(() => {
    if (swiperRef?.current) {
      swiperRef.current.slideTo(currentStep);
      if (swiperRef?.current?.isEnd) setReadyToSubmit(true);
    }
  }, [currentStep, Object.keys(errors)]);

  useEffect(() => {
    handleFormReset();
  }, [resetForm]);

  const pages = [
    {
      title: t('components.organisms.contactForm.steps.location'),
      content: (
        <>
          <Select
            label={t('components.organisms.contactForm.buildingLabel')}
            name="building"
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
            elements={buildingsSelectValues}
            defaultValue={{
              label: defaultBuilding?.label,
              value: defaultBuilding?.id.toString(),
            }}
          />
          <Select
            label={t('components.organisms.contactForm.accommodationLabel')}
            name="accommodation"
            defaultValue={defaultApartment}
            elements={(
              mapOfAccommodation.get(parseInt(watchBuilding?.toString())) ?? []
            ).filter(
              (item, index, self) =>
                index === self.findIndex((t) => t.value === item.value)
            )}
          />
        </>
      ),
      fieldsToValidate: ['building'],
    },
    {
      title: t('components.organisms.contactForm.steps.personalData'),
      content: (
        <>
          <Input
            label={t('components.organisms.contactForm.nameLabel')}
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
            name="name"
            type="text"
            placeholder={t('components.organisms.contactForm.namePlaceholder')}
          />

          <Input
            label={t('components.organisms.contactForm.lastNameLabel')}
            name="lastName"
            type="text"
            placeholder={t(
              'components.organisms.contactForm.lastNamePlaceholder'
            )}
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
          />
        </>
      ),
      fieldsToValidate: ['name', 'lastName'],
    },
    {
      title: t('components.organisms.contactForm.steps.contact'),
      content: (
        <>
          <Input
            label={t('components.organisms.contactForm.emailLabel')}
            name="email"
            type="email"
            placeholder={t('components.organisms.contactForm.emailPlaceholder')}
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
            onBlur={() => {
              validateEmails(getValues('email'), getValues('emailConfirm'));
            }}
          />

          <Input
            label={t('components.organisms.contactForm.emailConfirmLabel')}
            name="emailConfirm"
            type="email"
            placeholder={t('components.organisms.contactForm.emailPlaceholder')}
            requiredMessage={t(
              'components.organisms.contactForm.emailConfirmationRequiredMessage'
            )}
            onBlur={() => {
              validateEmails(getValues('email'), getValues('emailConfirm'));
            }}
          />

          <PhoneInput
            label={t('components.organisms.contactForm.phoneLabel')}
            name="telephone"
            language={lang === 'es' ? 'es' : 'eng'}
            requiredMessage={t(
              'components.organisms.contactForm.requiredMessage'
            )}
            className="h-full"
            setPhoneCountry={setPhoneCountry}
          />
        </>
      ),
      fieldsToValidate: ['email', 'emailConfirm', 'telephone'],
    },
    {
      title: t('components.organisms.contactForm.steps.sourceAndNotes'),
      content: (
        <>
          <Select
            label={t('components.organisms.contactForm.hearAboustUs')}
            name="hearUs"
            defaultValue={hearUsDefaultValue}
            {...hearUsSelect}
          />
          <TextArea
            label={t('components.organisms.contactForm.addMore')}
            rows={5}
            name="addMore"
            placeholder={t(
              'components.organisms.contactForm.addMorePlaceholder'
            )}
          />
        </>
      ),
    },
    {
      title: t('components.organisms.contactForm.steps.lastStep'),
      content: (
        <>
          <Checkbox
            text={t('components.organisms.contactForm.marketingOptIn')}
            name="marketingOptIn"
          />
          <Checkbox
            text={t('components.organisms.contactForm.marketingGreystar')}
            name="marketingGreystar"
          />
        </>
      ),
    },
  ];

  const pageNavigation = (
    <div className="flex w-full items-center justify-center gap-1 border-b border-state-primary-disabled p-4">
      {pages.map((page, index) => (
        <React.Fragment key={index}>
          {index !== 0 && (
            <hr
              className={`h-1 w-[5%] border-0 ${
                index < currentStep && 'bg-state-primary-default'
              } ${index > currentStep && 'bg-state-primary-disabled'}`}
              style={
                index === currentStep
                  ? {
                      backgroundImage:
                        'linear-gradient(to right, var(--tw-color-state-primary-default) 50%, var(--tw-color-state-primary-disabled) 50%)',
                    }
                  : {}
              }
            />
          )}
          <span
            className={`flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-state-primary-default text-xs ${
              index <= currentStep
                ? 'bg-state-primary-default text-text-alternative'
                : 'bg-transparent text-text-primary'
            }`}
            onClick={() => handleStep(index)}
          >
            {index + 1}
          </span>
        </React.Fragment>
      ))}
    </div>
  );

  useEffect(() => {
    handleFormReset();
  }, []);

  return (
    <>
      <Swiper
        className="w-full"
        slidesPerView={1}
        allowTouchMove={false}
        autoHeight={true}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {pages.map((page, index) => (
          <SwiperSlide key={index}>
            <div className="flex flex-col items-center justify-center gap-8">
              <div className="flex w-full flex-col justify-center text-center">
                {readyToSubmit && pageNavigation}
                <h3 className="text-heading-mobile--h3 p-4">{page.title}</h3>
              </div>
              <div className="flex w-full flex-col items-center justify-center gap-8">
                <div className="flex w-full flex-col gap-4">
                  {page.content}
                  {hasRequiredFields && (
                    <p className="mb-1 w-full text-[9px] uppercase tracking-wider">
                      {t('components.organisms.contactForm.obligatoryFields')}
                    </p>
                  )}
                </div>
              </div>
              {currentStep < pages.length - 1 ? (
                <div className="flex w-full flex-col-reverse gap-4 pb-1 sm:w-fit sm:flex-row">
                  <Button
                    type="button"
                    size="large"
                    variant="outline"
                    className="w-full sm:max-w-max"
                    disabled={currentStep === 0}
                    onClick={() => handleStep(currentStep - 1)}
                  >
                    {t('components.organisms.contactForm.landing.back')}
                  </Button>
                  <Button
                    type="button"
                    size="large"
                    variant="primary"
                    className="w-full sm:max-w-max"
                    onClick={() => handleStep(currentStep + 1)}
                  >
                    {t('components.organisms.contactForm.landing.next')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="submit"
                  size="large"
                  variant="secondary"
                  className="w-full max-w-[244px]"
                >
                  {t('components.organisms.contactForm.sendButton')}
                </Button>
              )}
            </div>
          </SwiperSlide>
        ))}
        <div className="mt-8 flex items-start justify-between gap-2 overflow-hidden transition-all duration-500">
          <SeeMore expanded={false} heigthPx={50} hideLabel icon="ArrowDownPri">
            <Prose
              html={textPolicies}
              className="text-other--detail text-xs leading-4 !text-primary-200"
            />
          </SeeMore>
        </div>
      </Swiper>
    </>
  );
};

ContactFormLanding.displayName = 'ContactFormLanding';
